import Button from '@mui/material/Button';
import format from 'date-fns/format';
import useOnScreen from 'packages/workspace/modules/helpers/hooks/useOnScreen';
import type { ChatConversationMessage } from 'packages/workspace/services/models/internal-channel';
import { Children, MutableRefObject, useEffect, useLayoutEffect, useRef, useState } from 'react';
import { Message } from '../message/Message';
import { useGetPastConversation } from 'packages/workspace/modules/datastores/internal-chat/useGetConversation';
import useInternalConversationStore from 'packages/workspace/services/store/omnichannel/internal-conversation-store';

export interface MessageListProps {
  messages: Array<ChatConversationMessage> | undefined;
  userId: string;
  type: string;
  task_sid?: string;
  app_id?: string;
  lastMessageRef?: MutableRefObject<HTMLLIElement | undefined>;
  conversation_id?: string;
  fromInternalChat?: boolean;
}

const MessageList: React.FC<MessageListProps> = (props) => {
  const {
    type,
    messages = [],
    lastMessageRef = {
      current: null,
    },
    conversation_id,
    fromInternalChat = false,
  } = props;
  const msgClass = type === 'onetoone' ? 'bg-tc-7' : 'bg-tc-1';
  const getTime = (startTime: number = Date.now()) => `${format(new Date(startTime), 'MM/dd/yyyy hh:mm aaa')}`;
  const messagesEndRef = useRef<HTMLLIElement>(null);
  const isVisible = useOnScreen(messagesEndRef);
  const scrollRef = useRef<HTMLDivElement>(null);
  const [enabled, setEnabled] = useState(false);
  const internalConvState = useInternalConversationStore();
  const { data: previousData, fetchNextPage, isFetching, isFetchingNextPage } = useGetPastConversation(conversation_id, internalConvState.ongoing_messages[conversation_id]?.[0]?.timestamp, enabled);

  useEffect(() => {
    lastMessageRef.current = messagesEndRef.current;
  }, [lastMessageRef]);

  //! Smooth scroll if the person sent last message.
  useLayoutEffect(() => {
    if (isVisible) {
      setTimeout(() => {
        messagesEndRef.current?.scrollIntoView({ behavior: 'smooth', block: 'end' });
      }, 300);
    }
  }, [isVisible, messages?.length]);

  const handleScroll = () => {
    if (fromInternalChat && scrollRef.current?.scrollHeight > scrollRef.current?.clientHeight && scrollRef.current?.scrollTop === 0 && messages?.length !== 0) {
      if (previousData?.pages[previousData?.pages.length - 1].data.messages?.length === 0) {
        setEnabled(false);
      } else {
        if (!isFetching && !isFetchingNextPage) {
          setEnabled(true);
          if (conversation_id !== '' && conversation_id !== undefined) {
            fetchNextPage();
          }
        }
        scrollRef.current.scrollTop += 50;
        setEnabled(false);
      }
    }
  };

  return (
    <>
      <div ref={scrollRef} onScroll={handleScroll} className={'flex flex-1 flex-col overflow-y-auto w-full ' + ` ${msgClass}`}>
        {!isVisible && (
          <Button className="rhs-scroll-to-new" type="button" size="small" onClick={() => messagesEndRef.current?.scrollIntoView({ behavior: 'smooth', block: 'end' })}>
            Scroll To Bottom
          </Button>
        )}
        <div className="flex-1">
          <ul className="chat-list-container left-chat">
            {Children.toArray(
              messages.map((msg: ChatConversationMessage, index: number) => (
                <Message
                  key={index}
                  dateTime={getTime(msg.timestamp)}
                  sender={msg.sender}
                  message={msg.message}
                  userId={props.userId}
                  type={type}
                  system={msg.system}
                  display_name={msg.display_name || ''}
                  message_type={msg.message_type}
                  action_owner={msg.action_owner}
                  helper_text={msg.helper_text}
                  entity_id={msg.entity_id}
                  task_sid={props.task_sid}
                  app_id={props.app_id}
                />
              ))
            )}
            <li className="h-2 mb-2" ref={messagesEndRef} />
          </ul>
        </div>
      </div>
    </>
  );
};
export default MessageList;
